/**
 * WP SRO Extractor — LLM extraction of (entity, attribute, query_frame) from WP posts.
 *
 * Uruchamiany jednorazowo per wpis po sync. Cached w DB (sro_extracted_at).
 * Prompt: thematic_prompts.step='wp_sro_extraction' (gpt-4.1-mini, temp 0.1, json_schema).
 *
 * Wejście: {wp_title, wp_first_100_words}. Wyjście: extracted_entity/attribute/query_frame
 * + first_paragraph_answer + supporting_entities + sro_extracted_at w wp_posts.
 *
 * Koszt: ~$0.0003 per wpis (gpt-4.1-mini). 88 wpisów ≈ $0.03.
 */

import { supabase } from "@/integrations/supabase/client";
import { loadPrompt, fillTemplate } from "@/services/llm/promptLoader";
import { openaiProvider } from "@/services/llm/openaiProvider";
import { logPublic, logDebug, logError } from "@/services/contentEngine/contentLogger";
import type { QueryFrame } from "@/types/thematicMaps";

const PREFIX = "[WP-SRO]";
const CONCURRENCY = 5; // equivalent requests

const WP_SRO_SCHEMA: Record<string, unknown> = {
  type: "object",
  properties: {
    entity: { type: "string" },
    attribute: { type: "string" },
    query_frame: {
      type: "string",
      enum: ["definitional", "procedural", "comparative", "causal", "evaluative", "mixed"],
    },
    first_paragraph_answer: { type: "string" },
    supporting_entities: {
      type: "array",
      items: { type: "string" },
    },
    confidence: { type: "number" },
    reasoning: { type: "string" },
  },
  required: [
    "entity",
    "attribute",
    "query_frame",
    "first_paragraph_answer",
    "supporting_entities",
    "confidence",
    "reasoning",
  ],
  additionalProperties: false,
};

interface SroExtractionResult {
  success: boolean;
  entity?: string;
  attribute?: string;
  query_frame?: QueryFrame;
  first_paragraph_answer?: string;
  supporting_entities?: string[];
  confidence?: number;
  reasoning?: string;
  cost_usd: number;
  tokens_in: number;
  tokens_out: number;
  error?: string;
  compiled_prompt?: string;
  response_text?: string;
}

interface ExtractableRow {
  id: string;
  title: string;
  first_100_words: string | null;
}

async function extractOne(
  row: ExtractableRow,
  template: string,
  modelId: string,
  temperature: number,
): Promise<SroExtractionResult> {
  if (!row.first_100_words || row.first_100_words.trim().length === 0) {
    return {
      success: false,
      error: "empty first_100_words",
      cost_usd: 0,
      tokens_in: 0,
      tokens_out: 0,
    };
  }

  const prompt = fillTemplate(template, {
    wp_title: row.title || "",
    wp_first_100_words: row.first_100_words,
  });

  try {
    const response = await openaiProvider.call(prompt, {
      model: modelId,
      temperature,
      jsonSchema: WP_SRO_SCHEMA,
    });

    const parsed = JSON.parse(response.text) as {
      entity: string;
      attribute: string;
      query_frame: QueryFrame;
      first_paragraph_answer: string;
      supporting_entities: string[];
      confidence: number;
      reasoning: string;
    };

    return {
      success: true,
      entity: parsed.entity,
      attribute: parsed.attribute,
      query_frame: parsed.query_frame,
      first_paragraph_answer: parsed.first_paragraph_answer,
      supporting_entities: parsed.supporting_entities,
      confidence: parsed.confidence,
      reasoning: parsed.reasoning,
      cost_usd: response.costUsd ?? 0,
      tokens_in: response.tokensInput,
      tokens_out: response.tokensOutput,
      compiled_prompt: prompt,
      response_text: response.text,
    };
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    return {
      success: false,
      error: msg,
      cost_usd: 0,
      tokens_in: 0,
      tokens_out: 0,
      compiled_prompt: prompt,
    };
  }
}

/**
 * Extract SRO for single wp_post (podaj UUID wp_posts.id).
 */
export async function extractSroForWpPost(wpPostId: string): Promise<SroExtractionResult> {
  const prompt = await loadPrompt("wp_sro_extraction");
  if (!prompt) {
    return {
      success: false,
      error: "Prompt wp_sro_extraction nie jest aktywny w bazie",
      cost_usd: 0,
      tokens_in: 0,
      tokens_out: 0,
    };
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data: row } = await (supabase as any)
    .from("wp_posts")
    .select("id, title, first_100_words")
    .eq("id", wpPostId)
    .maybeSingle();

  if (!row) {
    return {
      success: false,
      error: "Wpis nie istnieje",
      cost_usd: 0,
      tokens_in: 0,
      tokens_out: 0,
    };
  }

  const result = await extractOne(
    row as ExtractableRow,
    prompt.template,
    prompt.modelId || "gpt-4.1-mini",
    prompt.temperature ?? 0.1,
  );

  if (result.success) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    await (supabase as any)
      .from("wp_posts")
      .update({
        extracted_entity: result.entity,
        extracted_attribute: result.attribute,
        extracted_query_frame: result.query_frame,
        first_paragraph_answer: result.first_paragraph_answer,
        supporting_entities: result.supporting_entities ?? [],
        sro_extracted_at: new Date().toISOString(),
      })
      .eq("id", wpPostId);
  }

  return result;
}

export interface SroLogType {
  type: "info" | "success" | "error" | "stage";
}

export interface SroExtractOptions {
  onProgress?: (current: number, total: number) => void;
  onLog?: (type: "info" | "success" | "error" | "stage", message: string, debug?: Record<string, unknown>) => void;
  onlyMissing?: boolean; // default true — tylko te gdzie sro_extracted_at IS NULL
  concurrency?: number; // default 5
}

export interface SroBatchStats {
  processed: number;
  succeeded: number;
  failed: number;
  cost_usd: number;
  tokens_in: number;
  tokens_out: number;
  duration_ms: number;
}

/**
 * Batch extract SRO dla wszystkich wpisów w website.
 * Concurrency 5 (równoległe requesty).
 */
export async function extractSroForWebsite(
  websiteId: string,
  options: SroExtractOptions = {},
): Promise<SroBatchStats> {
  const t0 = Date.now();
  const onlyMissing = options.onlyMissing ?? true;
  const concurrency = options.concurrency ?? CONCURRENCY;

  const prompt = await loadPrompt("wp_sro_extraction");
  if (!prompt) {
    throw new Error("Prompt wp_sro_extraction nie jest aktywny w bazie");
  }

  // Load candidates
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let q = (supabase as any)
    .from("wp_posts")
    .select("id, title, first_100_words, sro_extracted_at")
    .eq("website_id", websiteId)
    .not("first_100_words", "is", null);

  if (onlyMissing) {
    q = q.is("sro_extracted_at", null);
  }

  const { data, error } = await q;
  if (error) throw error;

  const rows = (data || []) as Array<ExtractableRow & { sro_extracted_at: string | null }>;
  const total = rows.length;

  logPublic(PREFIX, `Start SRO extraction — ${total} wpisów (concurrency=${concurrency})`);
  options.onLog?.("info", `▶ SRO extraction: ${total} wpisów do przetworzenia (concurrency ${concurrency})`);

  const stats: SroBatchStats = {
    processed: 0,
    succeeded: 0,
    failed: 0,
    cost_usd: 0,
    tokens_in: 0,
    tokens_out: 0,
    duration_ms: 0,
  };

  // Simple semaphore: przetwarzaj chunk N równolegle
  const modelId = prompt.modelId || "gpt-4.1-mini";
  const temperature = prompt.temperature ?? 0.1;

  for (let i = 0; i < rows.length; i += concurrency) {
    const chunk = rows.slice(i, i + concurrency);
    const results = await Promise.all(
      chunk.map((row) => extractOne(row, prompt.template, modelId, temperature)),
    );

    for (let j = 0; j < chunk.length; j++) {
      const row = chunk[j];
      const r = results[j];
      stats.processed++;
      stats.cost_usd += r.cost_usd;
      stats.tokens_in += r.tokens_in;
      stats.tokens_out += r.tokens_out;

      if (r.success) {
        stats.succeeded++;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        await (supabase as any)
          .from("wp_posts")
          .update({
            extracted_entity: r.entity,
            extracted_attribute: r.attribute,
            extracted_query_frame: r.query_frame,
            first_paragraph_answer: r.first_paragraph_answer,
            supporting_entities: r.supporting_entities ?? [],
            sro_extracted_at: new Date().toISOString(),
          })
          .eq("id", row.id);
      } else {
        stats.failed++;
        logError(PREFIX, `Extract failed for ${row.id}: ${r.error}`);
      }
    }

    const pct = Math.round((stats.processed / total) * 100);
    if (total > 0 && (stats.processed % 10 === 0 || stats.processed === total || pct % 25 === 0)) {
      // Sample: ostatni udany z chunku — pelen prompt + response + extracted dane
      const lastOk = results.map((r, idx) => ({ r, row: chunk[idx] })).reverse().find((x) => x.r.success);
      options.onLog?.(
        "info",
        `  SRO progress ${pct}% (${stats.processed}/${total}) — success=${stats.succeeded}, failed=${stats.failed}, cost=$${stats.cost_usd.toFixed(4)}`,
        lastOk
          ? {
              sample_post_id: lastOk.row.id,
              sample_title: lastOk.row.title,
              extracted: {
                entity: lastOk.r.entity,
                attribute: lastOk.r.attribute,
                query_frame: lastOk.r.query_frame,
                first_paragraph_answer: lastOk.r.first_paragraph_answer,
                supporting_entities: lastOk.r.supporting_entities,
                confidence: lastOk.r.confidence,
                reasoning: lastOk.r.reasoning,
              },
              compiled_prompt: lastOk.r.compiled_prompt,
              response_text: lastOk.r.response_text,
            }
          : undefined,
      );
      logDebug(PREFIX, `progress ${stats.processed}/${total}`);
    }
    options.onProgress?.(stats.processed, total);
  }

  stats.duration_ms = Date.now() - t0;

  logPublic(
    PREFIX,
    `SRO done ${stats.duration_ms}ms: succeeded=${stats.succeeded} failed=${stats.failed} cost=$${stats.cost_usd.toFixed(4)}`,
  );
  options.onLog?.(
    "stage",
    `✓ SRO extraction done ${(stats.duration_ms / 1000).toFixed(1)}s: succeeded=${stats.succeeded}/${total}, cost=$${stats.cost_usd.toFixed(4)} (tokens in=${stats.tokens_in}, out=${stats.tokens_out})`,
    { stats },
  );

  return stats;
}
